import { useIsMobile } from '@dapp/multi-lib/ui/shared-hooks/useIsMobile';
import { getNetworkFromChainId } from '@dapp/multi-lib/network';
import { MessageStatus } from 'structure/bridge/constants/messageStatus';
import {
  DECIMALS_SHOW,
  DECIMALS_SHOW_MOBILE,
} from 'structure/bridge/constants/constants';
import { TTransaction } from 'structure/bridge/store/transactionsAtom';
import { firstDecimals } from 'structure/bridge/utils/bridgeStateHelpers';
import { formatUnits } from 'viem';
import { useConnection, useSwitchChain } from 'wagmi';
import { BridgeFailedTransactionRetryButton } from './BridgeFailedTransactionRetryButton';
import { BridgeFailedTransactionReverseButton } from './BridgeFailedTransactionReverseButton';

export const lzBaseUrl = 'https://layerzeroscan.com/tx/';

interface Props {
  transaction: TTransaction;
}

export function BridgeTransaction({ transaction }: Props) {
  const isMobile = useIsMobile();
  const { chain } = useConnection();
  const { switchChain } = useSwitchChain();
  // const [transactions, setTransactions] = useAtom(transactionsAtom);

  const isFailed = !!transaction?.failedMessage?._nonce;

  // Please leave for now - Alexander
  // const handleRemoveTxFromTransactions = () => {
  //   const newTransactions = transactions.filter(
  //     (tx) => tx.srcTxHash !== transaction.srcTxHash
  //   );
  //   setTransactions(newTransactions);
  // };

  return (
    <div
      className={`grid w-full ${
        isMobile
          ? 'grid-cols-[1.5rem_4rem_5rem_minmax(0,1fr)] gap-1 text-[8px]'
          : 'grid-cols-7 gap-2'
      } items-center text-xs text-white`}
    >
      {/* tx status icon */}
      <div className='col-span-1 flex justify-center'>
        <a
          href={`${lzBaseUrl}${transaction.srcTxHash}`}
          target='_blank'
          rel='noreferrer'
          className={`flex items-center justify-center rounded-xl py-2 text-white ${
            isMobile ? 'px-0' : 'px-4'
          }`}
        >
          {transaction.status === MessageStatus.INFLIGHT ? (
            <svg
              className={' h-5 w-5 animate-spin text-white'}
              xmlns='http://www.w3.org/2000/svg'
              fill='none'
              viewBox='0 0 24 24'
            >
              <circle
                className='opacity-25'
                cx='12'
                cy='12'
                r='10'
                stroke='currentColor'
                stroke-width='4'
              ></circle>
              <path
                className='opacity-75'
                fill='currentColor'
                d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
              ></path>
            </svg>
          ) : isFailed || transaction.status === MessageStatus.FAILED ? (
            <svg
              className='h-5 w-5 cursor-pointer text-red-500'
              xmlns='http://www.w3.org/2000/svg'
              fill='none'
              viewBox='0 0 24 24'
            >
              <path
                stroke='currentColor'
                stroke-width='2'
                d='M6 18L18 6M6 6l12 12'
              ></path>
            </svg>
          ) : (
            <svg
              className={'text-green-500 ml-2 mr-3 h-5 w-5'}
              xmlns='http://www.w3.org/2000/svg'
              fill='none'
              viewBox='0 0 24 24'
            >
              <path
                stroke='currentColor'
                stroke-width='2'
                d='M5 13l4 4L19 7'
              ></path>
            </svg>
          )}
        </a>
      </div>
      {/* token amount and symbol */}
      <div
        className={`col-span-1 flex whitespace-nowrap ${
          isMobile ? 'gap-1' : 'gap-2'
        }`}
      >
        <h4>
          {firstDecimals(
            parseFloat(
              formatUnits(
                BigInt(transaction.minAmount),
                transaction.token.decimals
              )
            ),
            isMobile ? DECIMALS_SHOW_MOBILE : DECIMALS_SHOW
          )}
        </h4>
        <h4>{transaction.token.symbol}</h4>
      </div>
      {/* source and destination networks */}
      <div className='col-span-1 flex justify-center whitespace-nowrap text-center'>
        <h1
          title={`${getNetworkFromChainId(transaction.srcChainId)?.name} to ${getNetworkFromChainId(transaction.dstChainId)?.name}`}
        >
          {getNetworkFromChainId(transaction.srcChainId)?.shortName} →{' '}
          {getNetworkFromChainId(transaction.dstChainId)?.shortName}
        </h1>
      </div>

      {/* retry reverse buttons for failed txs */}
      <div
        className={` ${isMobile ? 'col-span-1' : 'col-span-3'} flex justify-center`}
      >
        {isFailed &&
        chain?.id === transaction.dstChainId &&
        transaction.status === MessageStatus.DELIVERED ? (
          <div className={`flex ${isMobile ? 'gap-2' : 'gap-2'}`}>
            <BridgeFailedTransactionRetryButton transaction={transaction} />
            <BridgeFailedTransactionReverseButton transaction={transaction} />
          </div>
        ) : (
          isFailed &&
          transaction.status === MessageStatus.DELIVERED && (
            <button
              title='Reverse transaction'
              className={`border-1 flex items-center justify-center rounded-xl border ${isMobile ? 'px-2 py-0' : 'px-3 py-1'} text-white`}
              onClick={() => switchChain?.({ chainId: transaction.dstChainId })}
            >
              {!isMobile ? 'Switch network to retry' : 'Retry'}
            </button>
          )
        )}
        {!isFailed && (
          <div className='flex items-center justify-center text-white'>
            {transaction.status === MessageStatus.INFLIGHT
              ? 'In flight'
              : transaction.status === MessageStatus.FAILED
                ? 'Failed'
                : 'Bridged'}
          </div>
        )}
      </div>

      {/* explorer link */}
      {!isMobile && (
        <div className='col-span-1 flex justify-center'>
          {/* link to layer zero scan */}
          <button
            className='flex h-8 w-8 items-center justify-center text-white'
            title='view transaction on layer zero scan'
          >
            <a
              href={`${lzBaseUrl}${transaction.srcTxHash}`}
              target='_blank'
              rel='noreferrer'
              className='flex items-center justify-center rounded-xl px-4  py-2 text-white'
            >
              <span>
                <svg
                  viewBox='0 0 16 16'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                  className='h-64cursor-pointer w-4 text-white' // Add the 'text-white' class to make the icon white
                >
                  <path
                    d='M14.67 8a6.667 6.667 0 1 0-6.667 6.666m.666-13.3s2 2.634 2 6.634'
                    stroke='#fff'
                  ></path>
                  <path
                    d='M7.997 10.833h.5v-1h-.5v1Zm-.667 3.8.398-.302-.002-.004a1.396 1.396 0 0 1-.07-.098 7.103 7.103 0 0 1-.21-.33 10.584 10.584 0 0 1-.657-1.28 11.69 11.69 0 0 1-.959-4.62h-1c0 2.084.52 3.81 1.04 5.015.261.603.522 1.078.72 1.404a8.065 8.065 0 0 0 .339.513l.002.003a.01.01 0 0 1 0 .001l.399-.302ZM5.83 8c0-1.917.48-3.507.96-4.619.239-.555.478-.99.655-1.282a7.092 7.092 0 0 1 .28-.427l.003-.004-.398-.302-.398-.302-.001.001-.002.003a1.71 1.71 0 0 0-.027.036l-.07.1a8.06 8.06 0 0 0-.242.377c-.198.326-.459.8-.72 1.404A12.69 12.69 0 0 0 4.83 8h1Zm-4.08 2.833h6.247v-1H1.75v1Zm0-4.667h12.493v-1H1.75v1Z'
                    fill='white'
                  ></path>
                  <path d='m14 10.73-4 4' stroke='currentColor'></path>
                  <path
                    d='M10.998 10.287h3.463v3.463'
                    stroke='currentColor'
                    strokeLinecap='square'
                  ></path>
                </svg>
              </span>
            </a>
          </button>
          {/* reset failed checked tx X button */}
          {/* remove tx X button */}
          {/* <button
          onClick={handleRemoveTxFromTransactions}
          className='border-1 flex h-4 w-4 items-center justify-center rounded-full border text-white'
          title='remove transaction'
        >
          <svg
            className='h-2 w-2 cursor-pointer'
            xmlns='http://www.w3.org/2000/svg'
            fill='none'
            viewBox='0 0 24 24'
          >
            <path
              stroke='currentColor'
              stroke-width='2'
              d='M6 18L18 6M6 6l12 12'
            ></path>
          </svg>
        </button> */}
        </div>
      )}
    </div>
  );
}
